/* Copyright (c) 2020-present CloudIO Inc */

import { DBRow, IoPagesHistory } from '@cloudio-saas/datasource-types';
import { atom, useStateXAtom, useStateXAtomValue } from '@cloudio-saas/statex';
import { FormLabel, Theme, List, ListItem, ListItemText } from '@mui/material';
import { makeStyles } from '@mui/styles';
import VirtualList from 'components/DataGrid/grid/List';
import TimeAgo from 'react-timeago';
import Icon from 'icons/Icon';
import React, { CSSProperties } from 'react';
import Store from 'store/Store';
import useAppStore, { useRecord } from 'store/useAppStore';
import NoDataFound from 'utils/NoDataFound';
import { useAutoSizerContext } from 'hooks/useComponentSize';
import { MenuItemPulse } from 'utils/Pulse';
import { sidebarActivePageIdAtom } from 'layout/Sidebar/atoms';
import Checkbox from '../fields/Checkbox';

const useStyles = makeStyles((theme: Theme) => ({
  editIcon: {
    display: 'none',
  },
  listItem: {
    '&:hover': {
      backgroundColor: theme.palette.background.default,
    },
  },
  check: {
    minWidth: 42,
    maxWidth: 42,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  header: {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}));

export const selectionAtom = atom<[string | null, string | null]>({
  path: ['transient', 'comparePage'],
  defaultValue: [null, null],
  urlParam: true,
});

function AppCell({
  value,
  style,
  store,
}: {
  value: string;
  style?: CSSProperties;
  store: Store<IoPagesHistory>;
}) {
  const classes = useStyles();
  const { record } = useRecord(store, value);
  const recordsOrder = store.recordsOrder();
  const [selection, setSelection] = useStateXAtom(selectionAtom);

  const rec = record as DBRow<IoPagesHistory>;
  if (!record) return null;
  return (
    <ListItem className={classes.listItem} dense key={value} style={style}>
      <ListItemText
        primary={
          <>
            <TimeAgo date={rec.lastUpdateDate} /> by {rec.lastUpdatedBy}
          </>
        }
      />
      <Checkbox
        data-testid={rec.version}
        checked={selection[0] === rec.version || selection[1] === rec.version}
        className={classes.check}
        onChange={(e) => {
          const sel: [string | null, string | null] = [...selection];
          if (!e.target.checked) {
            if (sel[0] === rec.version) {
              sel[0] = '';
            } else if (sel[1] === rec.version) {
              sel[1] = '';
            }
          } else if (sel[1]) {
            sel[0] = sel[1];
            sel[1] = rec.version;
          } else if (sel[0]) {
            sel[1] = rec.version;
          } else {
            sel[0] = rec.version;
          }
          setSelection(sel);
        }}
      />
      {recordsOrder.length > 1 && recordsOrder[0] === value ? (
        <MenuItemPulse
          pulseKey="page_history_check"
          pulseMessage="Check any two versions to view the differences. The first selected version will be on the left in the diff viewer. To restore a specific version, select it and click on the restore icon, that appears on the top right corner of this panel."
        />
      ) : null}
    </ListItem>
  );
}

function getProps(appUid: string, pageUid: string) {
  return {
    filter: [{ appUid: { is: appUid } }, { pageUid: { is: pageUid } }],
    projection: {
      version: 1,
      lastUpdateDate: 1,
      lastUpdatedBy: 1,
    },
    sort: { lastUpdateDate: -1 },
    limit: 2000,
  };
}

const PagesHistory = ({ appUid }: { appUid: string }): React.ReactElement => {
  const { store, recordsOrder } = useAppStore<IoPagesHistory>({
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoPagesHistory',
    alias: 'IoPagesHistory-alias',
  });
  const activePageId = useStateXAtomValue(sidebarActivePageIdAtom);
  store.read({ props: getProps(appUid, activePageId) });
  const classes = useStyles();
  const { width, height } = useAutoSizerContext();

  if (recordsOrder.length === 0) {
    return (
      <NoDataFound message="You will see the history once you start making changes to this page." />
    );
  }

  return (
    <List>
      <ListItem className={classes.header} dense style={{ width, height: 40 }}>
        <ListItemText primary="Updated" />
        <FormLabel
          data-tip="Select any two version to view the diff"
          className={classes.check}
        >
          <Icon iconName="eye" />
        </FormLabel>
      </ListItem>
      <VirtualList
        CellComponent={({ rowIndex, style }) => (
          <AppCell style={style} value={recordsOrder[rowIndex]} store={store} />
        )}
        rowHeight={40}
        height={height - 40 - 16}
        totalRows={recordsOrder.length}
        width={width}
        custom
      />
    </List>
  );
};

export default PagesHistory;
